import { Crown } from "lucide-react";
import { useStripe } from "../contexts/StripeContext";
import { useUpgradePage } from "../hooks/useUpgradePage";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RichCaptionTextarea } from "./RichCaptionTextarea";
import { Textarea } from "./ui/textarea";

interface PostSettingsPanelProps {
  title: string;
  description: string;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  firstComment: string;
  onFirstCommentChange: (value: string) => void;
  /** Shown when the comment is empty: what posts if a channel turns it on. */
  defaultFirstComment?: string;
}

/**
 * The post's own content. Channels, their settings and timing live in the
 * publish modal; this is what every channel starts from.
 */
export function PostSettingsPanel({
  title,
  description,
  onTitleChange,
  onDescriptionChange,
  firstComment,
  onFirstCommentChange,
  defaultFirstComment,
}: PostSettingsPanelProps) {
  const { isStarterOrAbove } = useStripe();
  const { navigateToUpgrade } = useUpgradePage();

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background lg:border-r">
      <div className="flex items-center gap-2 border-b p-4">
        <h2 className="text-lg font-semibold">Post Settings</h2>
      </div>

      <div className="flex-1 space-y-6 overflow-y-auto p-4">
        <div className="space-y-2">
          <Label htmlFor="post-title">Title</Label>
          <Input
            id="post-title"
            placeholder="Internal post title..."
            className="notranslate"
            translate="no"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="post-desc">Description</Label>
          <RichCaptionTextarea
            id="post-desc"
            placeholder="What's on your mind?"
            textareaClassName="min-h-[240px] resize-none"
            value={description}
            onChange={onDescriptionChange}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="post-first-comment">First Comment</Label>
            {!isStarterOrAbove && (
              <span className="inline-flex items-center rounded-full bg-[#FF5C35]/10 p-1 text-[#FF5C35]">
                <Crown className="h-2.5 w-2.5" />
              </span>
            )}
          </div>
          <Textarea
            id="post-first-comment"
            placeholder={
              defaultFirstComment ||
              "e.g. your website URL, hashtags, or a call to action..."
            }
            className="min-h-[80px] resize-y"
            value={firstComment}
            onChange={(e) => onFirstCommentChange(e.target.value)}
            disabled={!isStarterOrAbove}
          />
          <p className="text-xs text-muted-foreground">
            {isStarterOrAbove ? (
              "Turn it on for each channel when you publish."
            ) : (
              <button
                type="button"
                onClick={() => navigateToUpgrade({ reason: "first_comment" })}
                className="cursor-pointer text-[#FF5C35] hover:underline"
              >
                Upgrade to auto-post a comment after publishing
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
